import type {Logro} from '@cnc/contracts';
import {useState} from 'react';
import {useLogros} from '../api/logros';
import {AYUDA_LOGROS as A} from '../componentes/ayuda-general';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {tr} from '../i18n/idioma';
import {fecha, numero} from '../utilidades/formato';

// Historial de logros de los exportadores: exportaciones y Big Bags que
// marcaron un numero redondo.
export function PaginaLogros() {
  const logros = useLogros();
  const [exportador, setExportador] = useState<string>('');
  const historial = logros.data?.historial ?? [];
  const exportadores = [...new Set(historial.map(l => l.exportador))].sort();
  const visibles = exportador ? historial.filter(l => l.exportador === exportador) : historial;

  return (
    <section className="max-w-4xl">
      <h1 className="text-2xl font-semibold text-verde-oscuro">{tr('Logros', 'Achievements')}</h1>
      <p className="text-sm text-apagado">
        {tr(
          'Cada exportador celebra su exportación 25, 50, 75, 100… (por el número del código) y su Big Bag 1, 100, 500, 1.000 y cada 1.000 más. Los logros recientes aparecen como aviso; puedes apagar los avisos en Mi cuenta.',
          'Each exporter celebrates its export number 25, 50, 75, 100… (by the number in the code) and its Big Bag 1, 100, 500, 1,000 and every 1,000 after that. Recent achievements pop up as a notice; you can turn notices off in My account.',
        )}
      </p>

      {exportadores.length > 1 && (
        <div className="mt-4 flex flex-wrap gap-1 text-sm">
          {['', ...exportadores].map(e => (
            <button
              key={e || 'todos'}
              type="button"
              onClick={() => setExportador(e)}
              className={`rounded-full border px-3 py-1 ${exportador === e ? 'border-verde bg-verde-claro text-verde-oscuro' : 'border-linea bg-white text-apagado'}`}
            >
              {e || tr('Todos', 'All')}
            </button>
          ))}
          <span className="self-center">
            <NotaDeAyuda texto={A.exportador} />
          </span>
        </div>
      )}

      {logros.isError && <p className="mt-4 text-sm text-alerta">{logros.error.message}</p>}
      {logros.data && !visibles.length && (
        <p className="mt-4 rounded-lg border border-linea bg-white p-4 text-sm text-apagado">
          {tr('Todavía no hay logros.', 'No achievements yet.')}
        </p>
      )}
      <ol className="mt-4 space-y-2">
        {visibles.map(l => (
          <Tarjeta key={l.id} logro={l} />
        ))}
      </ol>
    </section>
  );
}

function Tarjeta({logro: l}: {logro: Logro}) {
  return (
    <li
      className={`flex items-center gap-4 rounded-lg border bg-white px-4 py-3 ${l.grande ? 'border-dorado' : 'border-linea'}`}
    >
      <div
        aria-hidden
        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${l.grande ? 'bg-dorado-claro text-2xl' : 'bg-papel text-xl'}`}
      >
        {l.tipo === 'exportacion' ? '🚢' : '📦'}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-verde-oscuro">{l.titulo}</div>
        <div className="text-xs text-apagado">{l.detalle}</div>
      </div>
      <div className="text-right text-xs text-apagado">
        <div className="text-lg font-semibold text-tinta tabular-nums">
          {numero(l.numero)}
          <NotaDeAyuda texto={A.numero} />
        </div>
        <div>{fecha(l.fecha)}</div>
      </div>
    </li>
  );
}
